import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import type { Manifest } from '../util/manifest';
import { EpubJsBook } from '../types/epubjs';
import useNavigationStructureStore from './navigation-structure';
import usePositionTrackingStore from './position-tracking';

export type NavigationFlags = {
  toc: boolean;
  bookmark: boolean;
  location: boolean;
  lastmark: boolean;
  annotation: boolean;
};

const defaultNavigationFlags: NavigationFlags = {
  toc: false,
  bookmark: false,
  location: false,
  lastmark: false,
  annotation: false,
};

/**
 * NavigationStore
 *
 * UI-specific navigation state and convenience facade for accessing
 * NavigationStructureStore and PositionTrackingStore.
 *
 * Level 2 in the dependency hierarchy.
 */
const useNavigationStore = defineStore('book-player-navigation', () => {
  const navigationStructure = useNavigationStructureStore();
  const positionTracking = usePositionTrackingStore();

  // Re-export commonly used data from dependencies for convenience
  const {
    enhancedToc,
    pages,
    loi,
    hasPages,
    hasLoi,
    hasTableOfContents,
    hasAnnotations,
    isInitializing,
    resolutionProgress,
    navigationLevels,
    flattenedToc,
    totalPages,
    tocDependencyMap,
    pageToTocMap,
    firstAndLastPageNumber,
  } = storeToRefs(navigationStructure);

  const {
    currentPageIndex,
    currentPage,
    currentSpineIndex,
    currentPositionPct,
    currentAudioTrackHref,
    currentAudioTrackCFI,
    currentTocItem,
    totalSpineItems,
    getCurrentCFI,
  } = storeToRefs(positionTracking);

  // Delegate position tracking methods
  const setCurrentPageIndex = (index: number) => {
    positionTracking.setCurrentPageIndex(index);
  };

  const setSpineIndex = (index: number) => {
    positionTracking.setSpineIndex(index);
  };

  const setPositionPct = (percentage: number) => {
    positionTracking.setPositionPct(percentage);
  };

  const setAudioTrackPosition = (href: string | undefined, cfi?: string) => {
    positionTracking.setAudioTrackPosition(href, cfi);
  };

  const setTotalSpineItems = (total: number) => {
    positionTracking.setTotalSpineItems(total);
  };

  const setCFI = (cfi: string) => {
    positionTracking.setCFI(cfi);
  };

  const getPageIndexByCfi = (targetCfi: string, compareFn: (a: string, b: string) => number): number => {
    return positionTracking.getPageIndexByCfi(targetCfi, compareFn);
  };

  const setCurrentPageFromCFI = (currentCfi: string, compareFn: (cfiOne: string, cfiTwo: string) => number) => {
    positionTracking.setFromCFI(currentCfi, compareFn);
  };

  // UI-specific states
  const scrubPageIndex = ref<number | null>(null);
  const isScrubbing = ref(false);
  const isNavigating = ref(false);

  // Navigation flags to track user-initiated actions
  const navigationFlags = ref({ ...defaultNavigationFlags });
  const hasNavigationFlags = computed(() => Object.values(navigationFlags.value).some((flag) => flag));

  const setNavigationFlag = (flag: keyof typeof navigationFlags.value, value: boolean) => {
    navigationFlags.value[flag] = value;
  };

  const resetNavigationFlags = () => {
    navigationFlags.value = { ...defaultNavigationFlags };
  };

  // Delegate initialization to NavigationStructureStore
  const initializeNavigation = (
    m: Manifest,
    b: EpubJsBook,
    getElementCFI: (href: string, id: string) => Promise<string>,
  ) => {
    return navigationStructure.initializeNavigation(m, b, getElementCFI);
  };

  const initializeLoi = (b: EpubJsBook, spineItems: Manifest['spineItems']) => {
    return navigationStructure.initializeLoi(b, spineItems);
  };

  // Delegate NavigationStructure methods
  const setHasTableOfContent = (features: boolean) => {
    navigationStructure.setHasTableOfContent(features);
  };

  const setHasAnnotations = (features: boolean) => {
    navigationStructure.setHasAnnotations(features);
  };

  const findInNavigation = (search: string, flatten: boolean = false) => {
    return navigationStructure.findInNavigation(search, flatten);
  };

  const getTocByHref = (href: string) => {
    return navigationStructure.getTocByHref(href);
  };

  const getPageByHref = (href: string) => {
    return navigationStructure.getPageByHref(href);
  };

  const getActiveTocHrefByHref = (href: string) => {
    return navigationStructure.getActiveTocHrefByHref(href);
  };

  const getChapterTitleForHref = (href: string | null) => {
    return navigationStructure.getChapterTitleForHref(href);
  };

  const getChapterTitleForPageIndex = (index: number) => {
    return navigationStructure.getChapterTitleForPageIndex(index);
  };

  const setScrubbing = (index: number | null) => {
    scrubPageIndex.value = index;
    isScrubbing.value = index !== null;
  };

  const setNavigating = (val: boolean) => {
    isNavigating.value = val;
  };

  const $reset = () => {
    navigationStructure.$reset();
    positionTracking.$reset();
    scrubPageIndex.value = null;
    isScrubbing.value = false;
    isNavigating.value = false;
    navigationFlags.value = { ...defaultNavigationFlags };
  };

  return {
    // From the NavigationStructureStore
    enhancedToc,
    pages,
    loi,
    navigationLevels,
    isInitializing,
    resolutionProgress,
    totalPages,
    tocDependencyMap,
    pageToTocMap,
    flattenedToc,
    hasPages,
    hasLoi,
    hasTableOfContents,
    hasAnnotations,
    firstAndLastPageNumber,

    // From the PositionTrackingStore
    currentPageIndex,
    currentPage,
    currentSpineIndex,
    currentPositionPct,
    currentAudioTrackHref,
    currentAudioTrackCFI,
    currentTocItem,
    totalSpineItems,
    getCurrentCFI,

    scrubPageIndex,
    isScrubbing,
    isNavigating,
    navigationFlags,
    hasNavigationFlags,
    setScrubbing,
    setNavigating,
    setNavigationFlag,
    resetNavigationFlags,
    initializeNavigation,
    initializeLoi,
    setCurrentPageIndex,
    setSpineIndex,
    setPositionPct,
    setAudioTrackPosition,
    setCurrentPageFromCFI,
    getPageIndexByCfi,
    setTotalSpineItems,
    setCFI,
    setHasTableOfContent,
    setHasAnnotations,
    findInNavigation,
    getTocByHref,
    getPageByHref,
    getActiveTocHrefByHref,
    getChapterTitleForHref,
    getChapterTitleForPageIndex,
    $reset,
  };
});

export default useNavigationStore;
